'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { client } from '../../sanity/lib/client';

interface FooterLink {
    title: string;
    url: string | null;
    image?: {
        asset: {
            _ref: string;
            _type: string;
        };
        hotspot?: any;
        crop?: any;
    };
}

interface FooterSection {
    title: string;
    links: FooterLink[];
}

interface FooterData {
    sections: FooterSection[];
    copyright: string;
    socialLinks: FooterLink[];
}

const Footer: React.FC = () => {
    const [footerData, setFooterData] = useState<FooterData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Query GROQ per gestire le reference alle sezioni
    const query = `*[_type == "footer"] | order(orderRank) [0]{
        sections[]->{
            title,
            links[]{
                title,
                url,
                image
            }
        },
        copyright,
        socialLinks[]{
            title,
            url,
            image
        }
    }`;

    useEffect(() => {
        const fetchFooterData = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const data = await client.fetch<FooterData>(query);

                if (data) {
                    setFooterData(data);
                } else {
                    setError('Nessun dato footer trovato');
                }
            } catch (err) {
                console.error('Errore nel caricamento del footer:', err);
                setError('Errore nel caricamento del footer');

                // Fallback con dati di esempio
                setFooterData({
                    sections: [
                        {
                            title: 'Azienda',
                            links: [
                                { title: 'Chi siamo', url: '/chi-siamo' },
                                { title: 'Contatti', url: '/contatti' }
                            ]
                        }
                    ],
                    copyright: '© 2024 Il tuo sito. Tutti i diritti riservati.',
                    socialLinks: []
                });
            } finally {
                setIsLoading(false);
            }
        };

        fetchFooterData();
    }, []);

    if (isLoading) {
        return (
            <footer className="border-t bg-gray-50 py-10">
                <div className="container">
                    <div className="animate-pulse">
                        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
                            {[...Array(4)].map((_, i) => (
                                <div key={i} className="space-y-3 min-h-[160px]"> {/* 👈 Altezza minima */}
                                    <div className="h-4 bg-gray-200 rounded w-20"></div>
                                    <div className="space-y-2">
                                        <div className="h-3 bg-gray-200 rounded w-16"></div>
                                        <div className="h-3 bg-gray-200 rounded w-20"></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </footer>
        );
    }

    if (error && !footerData) {
        return (
            <footer className="border-t bg-gray-50 py-10">
                <div className="container">
                    <div className="text-center text-gray-500">
                        <p>Errore nel caricamento del footer</p>
                    </div>
                </div>
            </footer>
        );
    }

    if (!footerData) return null;

    return (
        <footer className="border-t bg-gray-50 py-10">
            <div className="container mx-auto px-4">
                <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
                    {footerData.sections?.map((section, index) => (
                        <div key={index} className="space-y-3 min-h-[160px]"> {/* 👈 Stessa altezza */}
                            <h3 className="font-bold text-gray-900">{section.title}</h3>
                            <ul className="space-y-2">
                                {section.links?.map((link, idx) => (
                                    link.url ? (
                                        <li key={idx}>
                                            <Link
                                                href={link.url.startsWith('/') ? link.url : `/${link.url}`}
                                                className="text-gray-600 hover:text-gray-900 hover:underline underline-offset-4 transition-colors"
                                                title={link.title}
                                            >
                                                {link.title}
                                            </Link>
                                        </li>
                                    ) : (
                                        <li key={idx} className="text-gray-600">
                                            {link.title}
                                        </li>
                                    )
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                {/* Copyright */}
                <div className="mt-10 pt-8 border-t border-gray-200">
                    <p className="text-xs sm:text-sm text-gray-500 text-center sm:text-left">
                        {footerData.copyright}
                    </p>
                </div>

                {/* Social Links */}
                {footerData.socialLinks && footerData.socialLinks.length > 0 && (
                    <div className="mt-6">
                        <ul className="flex items-center justify-center sm:justify-start gap-4">
                            {footerData.socialLinks.map((social, index) => (
                                social.url ? (
                                    <li key={index}>
                                        <Link
                                            href={social.url.startsWith('http') ? social.url : (social.url.startsWith('/') ? social.url : `/${social.url}`)}
                                            className="text-gray-600 hover:text-gray-900 transition-colors"
                                            title={social.title}
                                            target={social.url.startsWith('http') ? "_blank" : "_self"}
                                            rel={social.url.startsWith('http') ? "noopener noreferrer" : undefined}
                                        >
                                            <span className="sr-only">{social.title}</span>
                                            {/* Qui puoi aggiungere icone per i social */}
                                            {social.title}
                                        </Link>
                                    </li>
                                ) : null
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </footer>
    );
};

export default Footer;
